import type { Axis, AxisName } from "plotly.js";

/**
 * Layout helper functions to reduce repetitive code when building Plotly layouts
 */

/**
 * Calculate evenly spaced domains for multiple axis groups
 * @param numGroups Number of domain groups (e.g., 3 groups for 3 rows of axes)
 * @param gap Gap between groups (default 0.02)
 * @returns Array of [start, end] domain pairs from bottom to top
 *
 * @example
 * // Create 4 evenly spaced rows
 * const domains = calculateDomains(4);
 * // Returns: [[0, 0.2425], [0.2525, 0.495], [0.505, 0.7475], [0.7575, 1]]
 */
export function calculateDomains(
	numGroups: number,
	gap: number = 0.02,
): Array<[number, number]> {
	if (numGroups <= 0) return [];
	if (numGroups === 1) return [[0, 1]];
	const domains: Array<[number, number]> = [];
	const totalGaps = (numGroups - 1) * gap;
	const availableSpace = 1 - totalGaps;
	const groupHeight = availableSpace / numGroups;

	for (let i = 0; i < numGroups; i++) {
		const start = i * (groupHeight + gap);
		const end = start + groupHeight;
		domains.push([Number(start.toFixed(4)), Number(end.toFixed(4))]);
	}
	return domains;
}

/**
 * Title getter interface for axis function
 */
export interface TitleGetter {
	get(fieldName: string): string;
}

/**
 * Wrap text at word boundaries after a target character count
 * @param text Text to wrap
 * @param maxChars Target characters per line (default 15)
 * @returns Text with <br> tags inserted at word boundaries
 */
function wrapTitle(text: string, maxChars: number = 13): string {
	const words = text.split(" ");
	const lines: string[] = [];
	let currentLine = "";

	for (const word of words) {
		const testLine = currentLine ? `${currentLine} ${word}` : word;
		if (testLine.length > maxChars && currentLine) {
			lines.push(currentLine);
			currentLine = word;
		} else {
			currentLine = testLine;
		}
	}
	if (currentLine) {
		lines.push(currentLine);
	}

	return lines.join("<br>");
}

/**
 * Create an axis configuration
 * @param titleOrGetter Either a title string, or a TitleGetter to look up title from fieldName
 * @param color Color for title and tick labels
 * @param domain Vertical domain [start, end]
 * @param fieldName Field name for title lookup only (not stored on axis)
 * @param overlayingAxis If provided, axis is placed on right side overlaying this axis (e.g., "y", "y2")
 * @param options Additional axis options (range, minallowed, etc.)
 */
export function axis(
	titleOrGetter: string | TitleGetter,
	color: string,
	domain: [number, number],
	fieldName: string,
	overlayingAxis?: AxisName,
	options: Partial<Axis> = {},
): Partial<Axis> {
	const title =
		typeof titleOrGetter === "string"
			? titleOrGetter
			: titleOrGetter.get(fieldName);
	const base: Record<string, unknown> = {
		domain,
		title: { text: wrapTitle(title) },
		titlefont: { color },
		tickfont: { color },
		color, // Store color for easy access
		...options,
	};

	if (overlayingAxis) {
		base.side = "right";
		base.overlaying = overlayingAxis;
		// Plotly 4 defaults overlaying axes to tickmode "sync"; keep independent
		// auto ticks unless the axis defines its own (explicit "auto" ignores dtick)
		if (base.dtick === undefined && base.tickvals === undefined) {
			base.tickmode ??= "auto";
		}
	}

	return base as Partial<Axis>;
}

/**
 * Common axis option presets
 */
export const axisPresets = {
	/** For wind direction (0-360°) */
	windDirection: {
		range: [0, 360] as [number, number],
		minallowed: 0,
		maxallowed: 360,
		dtick: 90,
		gridcolor: "#339269",
		gridwidth: 2,
	},
	/** For percentage values (0-100) */
	percentage: {
		range: [0, 100] as [number, number],
		minallowed: 0,
		maxallowed: 100,
	},
} as const;
